<template>
  <div>
    <DashPeriodFilter
      :period="filters.period"
      :from="filters.from"
      :to="filters.to"
      :applied="appliedPeriod"
      :loading="loading"
      @change="onFilterChange"
    />

    <div v-if="loading && !loaded" class="text-center py-6 text-muted">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0">Carregando...</p>
    </div>

    <div v-else-if="errored" class="text-center py-6 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-2">{{ errorMessage || "Não foi possível carregar os dados." }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetch">Tentar novamente</button>
    </div>

    <div v-else>
    <div class="row g-3 mb-3">
      <div class="col-12 col-md-6">
        <div class="card">
          <div class="card-body">
            <p class="text-muted small mb-1">Valor médio das consultas</p>
            <span class="h2 fw-bold">{{ moneyBR(data.average_session_value_scheduled) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas com sucesso (mensal)">
          <ChartBar :data="monthlySuccessful" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas diárias">
          <ChartLine :data="dailySuccessful" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Psiapp+ vs Gratuito">
          <ChartBar :data="subscribersVsNon" :options="stackedOptions" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Recorrente vs Avulso">
          <ChartBar :data="recurringVsSingle" :options="stackedOptions" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Parceria vs Geral">
          <ChartBar :data="partnershipVsNot" :options="stackedOptions" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Pacientes novos vs antigos">
          <ChartBar :data="newVsOldPatient" :options="stackedOptions" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Status de cobrança">
          <ChartBar :data="billingStatus" :options="stackedOptions" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas por tipo">
          <ChartBar :data="byType" :options="stackedOptions" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Métodos de pagamento">
          <ChartBar :data="paymentMethod" :options="stackedOptions" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas: Média diária (mensal)">
          <ChartLine :data="dailyAverage" />
        </WidgetCard>
      </div>

      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas mensais com pacientes distintos">
          <ChartLine :data="distinctPatientsMonthly" />
        </WidgetCard>
      </div>
      <div class="col-12 col-xl-6">
        <WidgetCard title="Consultas com sucesso (anual)">
          <ChartBar :data="yearlySuccessful" />
        </WidgetCard>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import {
  groupSeries,
  singleSeries,
  moneyBR,
  baseOptions,
  dateTicks,
} from "@/utils/dashboardCharts";
import WidgetCard from "./WidgetCard.vue";
import DashPeriodFilter from "./DashPeriodFilter.vue";

export default {
  name: "DashAppointments",
  components: { WidgetCard, DashPeriodFilter },
  data() {
    return {
      loading: false,
      loaded: false,
      errored: false,
      errorMessage: "",
      filters: { period: "last_12_months", from: "", to: "" },
      appliedPeriod: null,
      data: {},
      stackedOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ stacked: true, ticks: dateTicks }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
        },
      },
    };
  },
  computed: {
    monthlySuccessful() {
      return singleSeries(this.data.monthly_successful || [], "year_month", "total", "Consultas", 0);
    },
    dailySuccessful() {
      return singleSeries(this.data.daily_successful || [], "day", "total", "Consultas", 2);
    },
    subscribersVsNon() {
      return groupSeries(this.data.subscribers_vs_non || [], "year_month", "subscription_status", "total");
    },
    recurringVsSingle() {
      return groupSeries(this.data.recurring_vs_single || [], "year_month", "recurrency_status", "total");
    },
    partnershipVsNot() {
      return groupSeries(this.data.partnership_vs_not || [], "year_month", "partnership_status", "total");
    },
    newVsOldPatient() {
      return groupSeries(this.data.new_vs_old_patient || [], "year_month", "paciente_novo", "total");
    },
    billingStatus() {
      const label = {
        succeeded: "Sucesso",
        failed: "Falhou",
        canceled: "Cancelado",
        pending: "Pendente",
        requires_action: "Requer Ação",
        requires_payment_method: "Falha",
      };
      const rows = (this.data.billing_status || []).map((r) => ({
        ...r,
        status: label[r.status] || r.status || "—",
      }));
      return groupSeries(rows, "year_month", "status", "total");
    },
    byType() {
      const typeLabel = (r) => {
        if (r.type === "imediate") return "Imediata (antiga)";
        if (r.first_appointment && r.online) return "Primeira sessão (imediata)";
        if (r.first_appointment) return "Primeira sessão (agendada)";
        if (r.online) return "Online (imediata)";
        return "Agendada";
      };
      const rows = (this.data.by_type || []).map((r) => ({ ...r, cat: typeLabel(r) }));
      return groupSeries(rows, "year_month", "cat", "total");
    },
    paymentMethod() {
      const label = { card: "Cartão", boleto: "Boleto", partnership: "Parceria" };
      const rows = (this.data.payment_method || []).map((r) => ({
        ...r,
        payment_method: label[r.payment_method] || "Outro",
      }));
      return groupSeries(rows, "year_month", "payment_method", "total");
    },
    dailyAverage() {
      return singleSeries(this.data.daily_average || [], "year_month", "daily_average", "Média/dia", 4);
    },
    distinctPatientsMonthly() {
      return singleSeries(this.data.distinct_patients_monthly || [], "year_month", "total", "Pacientes distintos", 5);
    },
    yearlySuccessful() {
      return singleSeries(this.data.yearly_successful_appointments || [], "year", "total", "Consultas", 0);
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    moneyBR,
    onFilterChange(f) {
      this.filters = f;
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      this.errored = false;
      try {
        const res = await this.$dashboardService.appointments(this.filters);
        this.data = res.data || {};
        this.appliedPeriod = res.period || null;
        this.loaded = true;
      } catch (e) {
        this.errored = true;
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

